/** Admin → Cost centres. Applied to the P&L side of every payroll journal. */
import { useCallback, useEffect, useState } from "react";
import { getCostCentres, saveCostCentre, type CostCentreRow } from "../../payroll/api";

export default function CostCentresTab() {
  const [rows, setRows] = useState<CostCentreRow[] | null>(null);
  const [draft, setDraft] = useState<Record<string, CostCentreRow>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setRows(await getCostCentres()); setDraft({}); } catch (e) { setError(msg(e)); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const edit = (r: CostCentreRow, patch: Partial<CostCentreRow>) =>
    setDraft((d) => ({ ...d, [r.code]: { ...r, ...d[r.code], ...patch } }));

  async function save(code: string) {
    const row = draft[code];
    if (!row) return;
    setError(null);
    try {
      await saveCostCentre({
        code: row.code, name: row.name, usedFor: row.used_for,
        isDefault: row.is_default, active: row.active,
      });
      setNotice(`${code} updated.`);
      await load();
    } catch (e) { setError(msg(e)); }
  }

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>Cost centres</h2>
          <p className="panel__sub">
            C9999 is the standing instruction for all payroll P&amp;L lines. Balance-sheet control
            lines carry no department or cost centre.
          </p>
        </div>
        <button className="btn" onClick={() => void load()}>Refresh</button>
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      {notice && !error && <div className="alert alert--ok">{notice}</div>}

      {rows === null ? <p className="muted">Loading…</p> : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Code</th><th>Name</th><th>Used for</th><th>Default</th><th>Active</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const d = draft[r.code] ?? r;
                const dirty = !!draft[r.code];
                return (
                  <tr key={r.code}>
                    <td className="mono">{r.code}</td>
                    <td><input className="input" value={d.name} onChange={(e) => edit(r, { name: e.target.value })} /></td>
                    <td><input className="input" value={d.used_for ?? ""} onChange={(e) => edit(r, { used_for: e.target.value })} /></td>
                    <td>
                      <input type="radio" name="default-cc" checked={d.is_default} onChange={() => edit(r, { is_default: true })} />
                    </td>
                    <td><input type="checkbox" checked={d.active} onChange={(e) => edit(r, { active: e.target.checked })} /></td>
                    <td><button className="btn btn--small" disabled={!dirty} onClick={() => void save(r.code)}>Save</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted">Changing the default affects future submissions only.</p>
    </section>
  );
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
